import React, { ReactElement, useCallback, useMemo, useState } from "react";

import { getVersionCategory } from "@/api/versionCategory";
import { useAppNotificationManager } from "@/components/AppProvider/AppNotificationProvider";
import { MenuItem, MenuList } from "@fluentui/react-components";
import {
  ChevronDownRegular,
  ChevronUpRegular,
  FlagRegular,
} from "@fluentui/react-icons";
import { useAppRouteParams } from "../../../../AppRoutes";
import { Sheet } from "../../../../domain/Sheet";
import { Version } from "../../../../domain/Version";
import { VersionCategoryRaw } from "../../../../domain/VersionCategories";
import { useDialogContextMenuItemsVersion } from "../../../Dialog/DialogFirestore/DialogVersion//hooks";
import { getVersionOptimizeStatusIcon } from "../../../OptimizeStatus/versionOptimizeStatusIcon";
import { TmpMenuSetOptimizeSummary } from "../../helpers";
import { SortableTmpMenuSet } from "../../helpers";
import styles from "./index.module.css";

import { useAppOrgsAndInvitedOrgs } from "@/components/AppProvider/AppOrgsAndInvitedOrgsProvider";
import { DialogPhaseType, useDialog } from "@/components/Dialog/helpers/hooks";
import { useAuthorizedSheets } from "@/hooks/useAuthorizedCollections";
import { translation } from "@/i18n";
import { nsVersion } from "@/i18n/locales/version";
import { buildVersionEntryPath } from "@/utils/versionPath";
import { useAppSideNavGlobalContext } from "../../providers/AppSideNavGlobalContextProvider";
import { useAppSideNavOpenState } from "../../providers/AppSideNavOpenStateProvider";
import { useContextMenuItemsCategory } from "./hooks";

const DEFAULT_LIST_LENGTH = 5;

const useTranslation = translation.use(nsVersion);

export const AppSideNavGlobalVersions: React.FC<{
  versionCategoryId: string;
  versions: Version[];
  versionCategories: VersionCategoryRaw[] | null;
  onChangePriorityVersion?: (
    categoryId: string,
    versionId: string | null
  ) => void;
}> = ({
  versionCategoryId,
  versions,
  versionCategories,
  onChangePriorityVersion,
}) => {
  const { t } = useTranslation();
  const { organizationId, versionId } = useAppRouteParams();
  const { organizations } = useAppOrgsAndInvitedOrgs();
  const targetVersionCategory = useMemo(
    () =>
      versionCategories?.find((category) => category.id === versionCategoryId),
    [versionCategories, versionCategoryId]
  );
  const organization = organizations.find(
    (org) => org.org_id === organizationId
  );
  const {
    categoriesMap,
    toggleIsCloseCategoryVersions,
    toggleShowAllCategoryVersions,
  } = useAppSideNavGlobalContext();
  const { openBothPanels } = useAppSideNavOpenState();
  const categoryState = categoriesMap.get(versionCategoryId);
  const { isClose, isShowAll } = categoryState ?? {
    isClose: false,
    isShowAll: false,
  };

  const { showSuccessNotification, showErrorNotification } =
    useAppNotificationManager();
  const { renderDialogsVersion, getContextMenuItemsVersion } =
    useDialogContextMenuItemsVersion();
  const {
    getContextMenuItems: getCategoryContextMenuItems,
    renderDeleteDialog,
  } = useContextMenuItemsCategory();
  const isVisibleMenu = organization?.is_sheet_enabled === true;
  const [priorityDialogPhase, setPriorityDialogPhase] =
    useState<DialogPhaseType>(null);
  const [priorityDialogTarget, setPriorityDialogTarget] = useState<{
    version: Version;
    isPriority: boolean;
  } | null>(null);
  const { renderDialogs: renderPriorityDialogs } = useDialog({
    dialogPhase: priorityDialogPhase,
    setDialogPhase: setPriorityDialogPhase,
  });

  // NOTE: 選択バージョンの比較基準サマリ
  const [sheets, isLoadedSheets] = useAuthorizedSheets(versionId);
  const compareTargetResultScoreSummary: Sheet | null = useMemo(
    () =>
      sheets.find(
        ({ sheetOptimizeType }) =>
          sheetOptimizeType === "result_score_summaries"
      ) ?? null,
    [sheets]
  );

  const versionsWithPriorityFirst = useMemo(() => {
    const priorityId = targetVersionCategory?.priority_version_id;
    if (!priorityId) return versions;
    const priorityVersion = versions.find(({ id }) => id === priorityId);
    if (!priorityVersion) return versions;
    return [priorityVersion, ...versions.filter(({ id }) => id !== priorityId)];
  }, [versions, targetVersionCategory?.priority_version_id]);

  const slicedVersions = useMemo(() => {
    return versionsWithPriorityFirst.slice(0, DEFAULT_LIST_LENGTH);
  }, [versionsWithPriorityFirst]);

  const handleClickPriority = useCallback(
    (version: Version, isPriority: boolean) => {
      if (!targetVersionCategory || !onChangePriorityVersion) return;
      setPriorityDialogTarget({ version, isPriority });
      setPriorityDialogPhase("confirm");
    },
    [targetVersionCategory, onChangePriorityVersion]
  );

  const handleConfirmChangePriority = useCallback(async () => {
    if (
      !priorityDialogTarget ||
      !targetVersionCategory ||
      !onChangePriorityVersion
    ) {
      setPriorityDialogPhase(null);
      return;
    }

    const latestCategory = await getVersionCategory({
      versionCategoryId,
      organizationId,
    });
    const latestPriorityId = latestCategory?.priority_version_id ?? null;
    const localPriorityId = targetVersionCategory?.priority_version_id ?? null;

    if (latestPriorityId !== localPriorityId) {
      const latestPriorityTitle = latestPriorityId
        ? versions.find(({ id }) => id === latestPriorityId)?.title ??
          t.version.unknownTitle()
        : null;
      const message = latestPriorityTitle
        ? t.version.priority.changedByOtherUserError({
            versionTitle: latestPriorityTitle,
          })
        : t.version.priority.clearedByOtherUserError();
      showErrorNotification(message);
      setPriorityDialogPhase(null);
      return;
    }

    const { version, isPriority } = priorityDialogTarget;
    try {
      await onChangePriorityVersion(
        versionCategoryId,
        isPriority ? null : version.id
      );
      showSuccessNotification(t.version.priority.updateSuccess());
    } catch (error) {
      showErrorNotification(t.version.priority.updateError(), error);
    } finally {
      setPriorityDialogTarget(null);
      setPriorityDialogPhase(null);
    }
  }, [
    t,
    onChangePriorityVersion,
    priorityDialogTarget,
    targetVersionCategory,
    versionCategoryId,
    organizationId,
    versions,
    showErrorNotification,
    showSuccessNotification,
  ]);

  const buildContextMenuForVersion = useCallback(
    (version: Version, isPriority: boolean): ReactElement => {
      const baseMenu = getContextMenuItemsVersion(version);
      if (!targetVersionCategory || !onChangePriorityVersion) return baseMenu;
      return (
        <MenuList>
          <MenuItem onClick={() => handleClickPriority(version, isPriority)}>
            {isPriority
              ? t.version.priority.unsetMenuItem()
              : t.version.priority.setMenuItem()}
          </MenuItem>
          {React.Children.toArray(baseMenu.props.children)}
        </MenuList>
      );
    },
    [
      t,
      getContextMenuItemsVersion,
      targetVersionCategory,
      onChangePriorityVersion,
      handleClickPriority,
    ]
  );

  return (
    <>
      {renderDeleteDialog()}
      <div className={styles.rootItems}>
        <span className={styles.closeOpenMenu}>
          <SortableTmpMenuSet
            id={versionCategoryId}
            text={`${targetVersionCategory?.name ?? t.version.category.uncategorized()} ( ${versions.length} )`}
            iconName={isClose ? "ChevronDown" : "ChevronUp"}
            onClick={() => toggleIsCloseCategoryVersions(versionCategoryId)}
            contextMenuItems={
              getCategoryContextMenuItems(targetVersionCategory || null) ??
              undefined
            }
          />
        </span>
        {!isClose ? (
          <div>
            {
              // パフォーマンス向上のため、デフォルトで表示するのは最新${DEFAULT_LIST_LENGTH}件のみ
              (isShowAll ? versionsWithPriorityFirst : slicedVersions).map(
                (version, index, arr) => {
                  const { Icon, color: iconColor = "#FFF" } =
                    getVersionOptimizeStatusIcon(version.optimizeStatus);
                  const isPriority =
                    version.id === targetVersionCategory?.priority_version_id;
                  const isLastItem = index === arr.length - 1;
                  return (
                    <React.Fragment key={version.id}>
                      {isPriority && (
                        <div className={styles.priorityVersionLabel}>
                          <FlagRegular
                            style={{
                              marginLeft: "20px",
                              width: "18px",
                              height: "18px",
                              flexShrink: 0,
                            }}
                          />
                          <span>{t.version.priority.label()}</span>
                        </div>
                      )}
                      <SortableTmpMenuSet
                        id={version.id}
                        text={version.title}
                        iconElement={
                          <Icon
                            style={{
                              display: "block",
                              width: "16px",
                              height: "16px",
                              color: iconColor,
                            }}
                          />
                        }
                        to={buildVersionEntryPath(
                          organizationId,
                          version.id,
                          version.initialViewId
                        )}
                        // initialViewId が設定されている時は特定ビューへ直接遷移するため、
                        // 両ナビの強制 open はしない
                        onClick={
                          version.initialViewId ? undefined : openBothPanels
                        }
                        isChildren={true}
                        active={version.id === versionId}
                        contextMenuItems={buildContextMenuForVersion(
                          version,
                          isPriority
                        )}
                        nestedElement={
                          isVisibleMenu ? (
                            <TmpMenuSetOptimizeSummary
                              version={version}
                              compareTargetResultScoreSummary={
                                compareTargetResultScoreSummary
                              }
                            />
                          ) : undefined
                        }
                      />
                      {isPriority && !isLastItem && (
                        <div className={styles.priorityVersionDivider} />
                      )}
                    </React.Fragment>
                  );
                }
              )
            }
          </div>
        ) : null}
        {!isClose && versions.length > DEFAULT_LIST_LENGTH && (
          <button
            className={styles.showAllVersionsButton}
            onClick={() => toggleShowAllCategoryVersions(versionCategoryId)}
          >
            {isShowAll ? (
              <ChevronUpRegular
                style={{
                  width: "20px",
                  height: "20px",
                  flexShrink: 0,
                }}
              />
            ) : (
              <ChevronDownRegular
                style={{
                  width: "20px",
                  height: "20px",
                  flexShrink: 0,
                }}
              />
            )}
            {isShowAll
              ? t.version.list.showLatestOnly({ count: DEFAULT_LIST_LENGTH })
              : t.version.list.showAll()}
          </button>
        )}
        {renderDialogsVersion(
          isShowAll ? versionsWithPriorityFirst : slicedVersions
        )}
        {priorityDialogTarget &&
          renderPriorityDialogs({
            confirm: (() => {
              const categoryName =
                targetVersionCategory?.name ??
                t.version.category.uncategorized();
              const currentPriorityTitle =
                targetVersionCategory?.priority_version_id &&
                targetVersionCategory.priority_version_id !==
                  priorityDialogTarget.version.id
                  ? versions.find(
                      ({ id }) =>
                        id === targetVersionCategory.priority_version_id
                    )?.title ?? null
                  : null;
              const setElement = (
                <>
                  <p style={{ marginBottom: "8px" }}>
                    {t.version.priority.setDialog.message({
                      categoryName,
                      versionTitle: priorityDialogTarget.version.title,
                    })}
                  </p>
                  {currentPriorityTitle ? (
                    <p style={{ color: "red", marginBottom: "8px" }}>
                      {t.version.priority.setDialog.currentPriorityNote({
                        versionTitle: currentPriorityTitle,
                      })}
                    </p>
                  ) : null}
                </>
              );
              const unsetElement = (
                <>
                  {t.version.priority.unsetDialog.message({
                    categoryName,
                    versionTitle: priorityDialogTarget.version.title,
                  })}
                </>
              );
              return {
                title: priorityDialogTarget.isPriority
                  ? t.version.priority.unsetDialog.title()
                  : t.version.priority.setDialog.title(),
                element: priorityDialogTarget.isPriority
                  ? unsetElement
                  : setElement,
                buttonText: priorityDialogTarget.isPriority
                  ? t.version.priority.unsetDialog.button()
                  : t.version.priority.setDialog.button(),
                onClick: handleConfirmChangePriority,
                onClickCancel: () => setPriorityDialogTarget(null),
              };
            })(),
          })}
      </div>
    </>
  );
};
